import React, { useEffect, useState } from "react";
import { useChatContext } from "stream-chat-react";
import { useAuth } from "../../../hooks/useAuth";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../../hooks/useTheme";
import { useRouter } from "../../../hooks/useRouter";
import { createChatChannelModel } from "../../../utils/ChatChannelUtils";
import { ChannelExtended } from "./ChatListTile";
import { ChatChannel, ChatChannelType } from "../../../models/ChatChannel";
import Clickable from "../../../components/Clickable";
import UserBadge from "./UserBadge";
import { ChatAvatar } from "./ChatAvatar";
import { plebsRepository } from "../../../repositories/PlebsRepository";
import { getDistanceBetweenPlebs, Pleb } from "../../../models/Pleb";
import { LayoutWithTopBar } from "../../../components/LayoutWithTopBar";
import { ButtonSendTo } from "../../../components/ButtonSendTo";
import { SendTransactionStateRecipient } from "../wallet/SendTransaction";
import { formatAmount, formatNumberWithCommas } from "../../../utils/Utils";
import { useStream } from "../../../hooks/useStream";

interface MessagePageTopBarProps {
    children: React.ReactNode;
    onClickSend?: (data: SendTransactionStateRecipient) => void;
}

const MessagePageTopBar: React.FC<MessagePageTopBarProps> = ({children, onClickSend}) => {
    const { channel } = useChatContext();
    const { pleb } = useAuth();
    const {isMobile} = useTheme();
    const {navigateToProfileFromChatChannel} = useRouter();
    const { startCall } = useStream();
    const navigate = useNavigate();
    const location = useLocation();
    const [distance, setDistance] = useState<string | null>(null);
    const [currentPleb, setCurrentPleb] = useState<Pleb | null>(null);
    const { tab, type, id } = useParams();

    const chatChannel = channel ? 
            createChatChannelModel(channel as ChannelExtended, pleb?.id || '') : null;

    useEffect(() => {
        if(chatChannel?.type === ChatChannelType.Messaging) {
            plebsRepository.getUserDetails(chatChannel?.id || '', pleb?.id || '')
                .then((otherPleb) => {
                    if(pleb && otherPleb) {
                        setDistance(getDistanceBetweenPlebs(otherPleb, pleb));
                        setCurrentPleb(otherPleb);
                    }
                })
                .catch((err) => {
                    console.error('err', err);
                });
        }
    }, [type, id]);

    const navigateToUserProfile = (chatChannel: ChatChannel | null) => {
        if(chatChannel) {
            navigateToProfileFromChatChannel(chatChannel);
        }
    }

    const renderSubTitle = () => {
        switch(chatChannel?.type) {
            case ChatChannelType.Group:
            case ChatChannelType.Event:
                return <div className="text-sm opacity-65">{formatAmount(chatChannel.memberCount)} member{chatChannel.memberCount !== 1 && 's'}</div>;
            case ChatChannelType.Messaging:
                return <div className="text-sm opacity-65">{distance}</div>;
            default:
                return null;
        }
    }

    const onClickPrevArrow = () => {
        const queryParams = new URLSearchParams(location.search);
        const urlBack = queryParams.get("urlGoBack");

        if(urlBack)
            return () => navigate(-1);

        if(isMobile)
            return () => navigate(`/home/${tab}`);

        return undefined;
    }
            
    return <LayoutWithTopBar
            topBarEndPart={
                <div className="flex flex-row items-center gap-1">
                    <ButtonSendTo 
                        pleb={currentPleb}
                        chatChannel={chatChannel}
                        onClickSend={onClickSend}
                        width={isMobile ? 35 : 50}
                    />
                    {
                        chatChannel?.type === ChatChannelType.Messaging && (
                            <Clickable 
                                className="cursor-pointer flex items-center justify-center rounded-xl h-[50px]" 
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    if(pleb && chatChannel) {
                                        startCall(pleb.id, chatChannel.id);
                                    }
                                }}
                                style={{ width: isMobile ? '35px' : '50px'}}
                            >
                                <span className="material-icons text-brand-orange text-[30px]">call</span>
                            </Clickable>
                        )
                    }
                </div>
            }
            onClickPrevArrow={onClickPrevArrow()}
            topBarChildren={
                <div className="flex flex-row items-center gap-1">
                    {
                        chatChannel && <div className="cursor-pointer" onClick={() => navigateToUserProfile(chatChannel)}>
                            <ChatAvatar channel={chatChannel} size={48} />
                        </div>
                    }
                    <Clickable className="flex flex-col items-start py-1 px-2 rounded-full" onClick={() => navigateToUserProfile(chatChannel)}>
                        <div className="inline-flex flex-row items-center gap-1">
                            <h1 className="text-l font-bold">{chatChannel?.name || ''}</h1>
                            {
                                chatChannel?.badge && (
                                    <div className="inline-block flex-shrink-0">
                                        <UserBadge badge={chatChannel.badge} />
                                    </div>
                                )
                            }
                        </div>
                        {renderSubTitle()}
                    </Clickable>
                </div>
            }
        >
            {children}
    </LayoutWithTopBar>
}

export default MessagePageTopBar;